import { useEffect, useMemo, useState } from "react";
import { Phone } from "lucide-react";
import { categoryOrder, sortMenuItems } from "@/constants/nossaData";
import { ComingSoonImage } from "@/components/nossa/ComingSoonImage";

function getDishIdFromUrl() {
  return new URLSearchParams(window.location.search).get("plato");
}

function getCategories(menu) {
  const availableCategories = new Set(menu.map((item) => item.category));
  return ["Todos", ...categoryOrder.filter((category) => availableCategories.has(category))];
}

function getVisibleItems(menu, activeCategory) {
  if (activeCategory === "Todos") return sortMenuItems(menu);
  return menu.filter((item) => item.category === activeCategory);
}

export default function MenuPage({ menu, menuNote }) {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [highlightedItem, setHighlightedItem] = useState("");
  const categories = useMemo(() => getCategories(menu), [menu]);
  const visibleItems = useMemo(() => getVisibleItems(menu, activeCategory), [activeCategory, menu]);

  useEffect(() => {
    const dishId = getDishIdFromUrl();
    if (!dishId || !menu.length) return;
    const dish = menu.find((item) => item.id === dishId);
    if (!dish) return;
    setActiveCategory(dish.category);
    setHighlightedItem(dishId);
    window.setTimeout(() => {
      document.querySelector(`[data-dish-id="${dishId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 180);
  }, [menu]);

  return (
    <section className="page-section" data-testid="menu-page">
      <MenuIntro />
      <p className="menu-note" data-testid="menu-allergen-note">{menuNote}</p>
      <CategoryFilter categories={categories} activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
      <DishGrid highlightedItem={highlightedItem} visibleItems={visibleItems} />
      <MenuReservationCta />
    </section>
  );
}

function MenuIntro() {
  return (
    <div className="page-intro menu-intro">
      <span className="eyebrow" data-testid="menu-page-eyebrow">Menú</span>
      <h1 data-testid="menu-page-title">NOSSA Carta</h1>
      <p data-testid="menu-page-description">Explora la carta completa de Nossa: desayunos, meriendas, raciones, hamburguesas, bocadillos y bebidas.</p>
    </div>
  );
}

function CategoryFilter({ categories, activeCategory, onCategoryChange }) {
  return (
    <div className="category-row" data-testid="menu-category-filter-row">
      {categories.map((category) => (
        <button key={category} data-testid={`menu-category-button-${category.toLowerCase().replaceAll(" ", "-")}`} className={activeCategory === category ? "category-pill active" : "category-pill"} onClick={() => onCategoryChange(category)}>
          {category}
        </button>
      ))}
    </div>
  );
}

function DishGrid({ visibleItems, highlightedItem }) {
  return (
    <div className="menu-grid" data-testid="menu-items-grid">
      {visibleItems.map((item) => <DishCard key={item.id} item={item} highlighted={highlightedItem === item.id} />)}
    </div>
  );
}

function MenuReservationCta() {
  return (
    <section className="menu-reservation-cta" data-testid="menu-reservation-cta-section">
      <span className="eyebrow" data-testid="menu-reservation-cta-eyebrow">Reserva</span>
      <h2 data-testid="menu-reservation-cta-title">¿Ya sabes qué pedir?</h2>
      <p data-testid="menu-reservation-cta-description">Reserva tu mesa por WhatsApp y te esperamos en Nossa.</p>
      <a className="menu-reservation-button" href="https://wa.me/34680502341?text=Hola%20Nossa%2C%20quiero%20reservar%20una%20mesa" target="_blank" rel="noreferrer" data-testid="menu-reservation-whatsapp-button">
        Reservar mesa <Phone size={18} />
      </a>
    </section>
  );
}

function DishCard({ item, highlighted }) {
  return (
    <article className={highlighted ? "dish-card highlighted" : "dish-card"} data-testid={`menu-item-card-${item.id}`} data-dish-id={item.id}>
      <div className="dish-image-wrap">
        {item.image && item.image !== "COMING_SOON" ? (
          <img src={item.image} alt={item.name} data-testid={`menu-item-image-${item.id}`} />
        ) : (
          <ComingSoonImage itemName={item.name} testId={`menu-item-image-${item.id}`} />
        )}
        <span data-testid={`menu-item-category-${item.id}`}>{item.category}</span>
      </div>
      <div className="dish-content">
        <div>
          <h2 data-testid={`menu-item-name-${item.id}`}>{item.name}</h2>
          <p data-testid={`menu-item-description-${item.id}`}>{item.description}</p>
        </div>
        <strong data-testid={`menu-item-price-${item.id}`}>{item.price}</strong>
      </div>
    </article>
  );
}